import { type CrownEnv } from "../crown-data-import/env/env-config";

/** The parameters that decide what a run touches, independent of its mode. */
export type PieceBackfillRunParams = {
  /** The target environment the run would execute against. */
  readonly env: CrownEnv;
  /** The company being backfilled. */
  readonly companyUuid: string;
  /** The eligibility cutoff, as an ISO timestamp. */
  readonly cutoffDate: string;
  /** The billing parties the run is narrowed to; empty means all of them. */
  readonly billingPartyContactUuids: readonly string[];
};

/**
 * Builds a stable identity for a set of run parameters, so an EXECUTE can be
 * matched against the DRY_RUN that previewed it. Billing parties are sorted, so
 * selection order does not invalidate a dry run.
 *
 * The env is part of the identity because the same company UUID and cutoff
 * describe different rows in different environments. Without it, a dry run
 * previewed against one env satisfied the execute gate for another, and the gate
 * reported agreement while the mutation went somewhere else.
 *
 * @param params The run parameters.
 * @returns A signature that is equal exactly when two runs would touch the same
 *   rows.
 */
export const buildRunSignature = (params: PieceBackfillRunParams): string => {
  const parties = [...params.billingPartyContactUuids].sort();
  return [
    params.env,
    params.companyUuid,
    params.cutoffDate,
    parties.join(","),
  ].join("|");
};

/**
 * Collapses whitespace so an operator who typed a trailing space or pasted a
 * name with a double space is not blocked by something invisible. Case is
 * preserved: the gate is meant to be deliberate.
 *
 * @param value The raw text.
 * @returns The value with edge whitespace removed and internal runs collapsed.
 */
const normalizeName = (value: string): string => {
  return value.trim().replaceAll(/\s+/g, " ");
};

/**
 * Whether the operator has typed the target company's name exactly, which is
 * the confirmation an EXECUTE requires.
 *
 * An empty company name never matches, so a company that failed to load cannot
 * be confirmed with an empty box.
 *
 * @param typed What the operator typed.
 * @param companyName The selected company's name.
 * @returns True when the two agree, ignoring only whitespace differences.
 */
export const isConfirmationMatch = (
  typed: string,
  companyName: string,
): boolean => {
  const expected = normalizeName(companyName);
  if (expected.length === 0) {
    return false;
  }
  return normalizeName(typed) === expected;
};

/**
 * Whether a value is a plain keyed object, so its fields can be read without a
 * cast.
 *
 * @param value The value to test.
 * @returns True for non-null, non-array objects.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

/** A finished run, as far as signature derivation is concerned. */
export type RunSignatureSource = {
  /** The company the run targeted. */
  readonly companyUuid: string;
  /** The run's stored parameters, an opaque JSON scalar. */
  readonly params: unknown;
};

/**
 * Derives a run's signature from the run itself rather than from the form.
 *
 * The form can drift while a run is in flight, so the signature a completed dry
 * run clears is read back off the run's own stored parameters: it is the only
 * record of what was actually planned.
 *
 * The env is passed in rather than read from the run, because a run's stored
 * params do not record it — the run lives in the database of the env it ran in,
 * so the caller must supply the env it fetched the run from.
 *
 * @param run The finished run.
 * @param env The env the run was fetched from.
 * @returns The signature, or `null` when the run's parameters are unreadable (in
 *   which case nothing is cleared and EXECUTE stays locked).
 */
export const signatureFromRun = (
  run: RunSignatureSource,
  env: CrownEnv,
): string | null => {
  const record = run.params;
  if (!isRecord(record)) {
    return null;
  }
  const { cutoffDate } = record;
  if (typeof cutoffDate !== "string") {
    return null;
  }
  const rawParties = record.billingPartyContactUuids;
  const billingPartyContactUuids = Array.isArray(rawParties)
    ? rawParties.filter((entry): entry is string => typeof entry === "string")
    : [];
  return buildRunSignature({
    env,
    companyUuid: run.companyUuid,
    cutoffDate,
    billingPartyContactUuids,
  });
};

/** Everything that decides whether EXECUTE is available. */
export type ExecuteGateState = {
  /** Signature of the parameters currently in the form. */
  readonly currentSignature: string;
  /**
   * Signature of the dry run that completed in this session, or `null` when no
   * dry run has completed yet.
   */
  readonly clearedSignature: string | null;
  /** True while a run is being started or is still in flight. */
  readonly busy: boolean;
};

/**
 * Whether the operator may open the execute confirmation.
 *
 * EXECUTE is gated on a DRY_RUN that completed in this session for the same
 * parameters: changing the company, the cutoff, or the billing-party selection
 * invalidates the preview and re-locks execute.
 *
 * @param state The current gate inputs.
 * @returns True when execute may be attempted.
 */
export const isExecuteUnlocked = (state: ExecuteGateState): boolean => {
  if (state.busy || state.clearedSignature === null) {
    return false;
  }
  return state.clearedSignature === state.currentSignature;
};
